/**
 * Error reporting to Sentry (ADR 0009). Only when VITE_SENTRY_DSN is set: otherwise the SDK chunk is never loaded.
 * No PII: no emails, IPs, cookies, request bodies, or auth headers.
 */
export type SentryEnv = {
  VITE_SENTRY_DSN?: string;
  VITE_SENTRY_ENVIRONMENT?: string;
  VITE_GIT_SHA?: string;
  MODE?: string;
};

type Scrubbable = {
  user?: { id?: string | number; [k: string]: unknown };
  request?: { cookies?: unknown; headers?: Record<string, string>; data?: unknown };
};

export function scrub<T extends Scrubbable>(event: T): T {
  if (event.user) event.user = event.user.id === undefined ? {} : { id: event.user.id };
  if (event.request) {
    delete event.request.cookies;
    delete event.request.data;
    if (event.request.headers) {
      const headers = { ...event.request.headers };
      for (const k of Object.keys(headers)) {
        if (/^(authorization|cookie)$/i.test(k)) delete headers[k];
      }
      event.request.headers = headers;
    }
  }
  return event;
}

/** Init options, or null when Sentry is off. */
export function sentryOptions(env: SentryEnv) {
  const dsn = env.VITE_SENTRY_DSN?.trim();
  if (!dsn) return null;
  return {
    dsn,
    environment: env.VITE_SENTRY_ENVIRONMENT?.trim() || env.MODE || 'production',
    release: env.VITE_GIT_SHA?.trim() || 'dev',
    sendDefaultPii: false,
    tracesSampleRate: 0,
    beforeSend: scrub,
  };
}

/** Starts Sentry if configured. Never throws: monitoring must not break the portal. */
export async function initSentry(env: SentryEnv = import.meta.env): Promise<boolean> {
  const options = sentryOptions(env);
  if (!options) return false;
  try {
    const Sentry = await import('@sentry/react');
    Sentry.init(options);
    return true;
  } catch {
    return false;
  }
}
